import AuthenticationLayout from 'components/AuthLayout';
import Button from 'components/Button';
import Error from 'components/form/Error';
import CountryInput from 'components/form/Inputs/CountryInput';
import Input from 'components/form/Inputs/Input';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import PasswordInput from 'components/form/Inputs/PasswordInput';
import Radio from 'components/form/Inputs/Radio';
import PasswordRequirements from 'components/form/PasswordRequirements';
import Head from 'components/profileNavigation/Head';
import { GenderValues } from 'enums/Genders';
import REGIONS from 'enums/Regions';
import { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { mutate } from 'swr';
import isPasswordValid from 'utils/isPasswordValid';
import publicAPI from 'utils/publicAxiosInstance';

const Subtitle = styled.div`
  color: var(--gray-500);
  margin-top: 2.25rem;
  font-size: 1rem;
  text-align: justify;
  p {
    margin-bottom: 1rem;
  }
  width: 60rem;
  max-width: 100%;
`;

const FormContainer = styled.div`
  width: 60rem;
  max-width: 100%;
  margin-top: 1.25rem;
`;

const Line = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--gray-200);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;

  button {
    margin-left: auto;
    margin-right: auto;
    margin-top: 3rem;
    margin-bottom: 4rem;
    padding: 0.7rem 1.1rem;
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const FormSection = styled.div`
  p {
    margin-top: 1.5rem;
  }
  width: 100%;

  @media (min-width: 900px) {
    &:first-child {
      margin-right: 3rem;
    }
  }
`;
const Label = styled.p`
  font-size: 0.9rem;
  margin-bottom: 0.375rem;
  color: var(--gray-700);
`;

const RadiosContainer = styled.div`
  padding: 0.8rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 0.8rem;
`;
const LabelledCheckboxContainer = styled.div`
  padding: 0.8rem 0;
`;

const GeneralErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.8rem;
`;

const EmailNotice = styled.p`
  color: var(--red-500, #dc2626);
  font-weight: 700;
  font-size: 0.95rem;
  text-align: center;
  margin-top: 2rem;
  margin-bottom: -1.5rem;
`;

const AgeHint = styled.p`
  font-size: 0.8rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
  margin-bottom: 0;
`;

type FormState = {
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  gender: GenderValues;
  address: string;
  region: string;
  postalCode: string;
  city: string;
  country: string;
  dateOfBirth: string;
  nationality: string;
  isHandicap: boolean;
  handicapDetails: string;
};

const initialState: FormState = {
  lastName: '',
  firstName: '',
  email: '',
  phone: '',
  password: '',
  passwordConfirm: '',
  gender: 'MALE',
  address: '',
  region: '',
  postalCode: '',
  city: '',
  country: '',
  dateOfBirth: '',
  nationality: '',
  isHandicap: false,
  handicapDetails: '',
};

type Errors = {
  general: boolean;
  passwordMatch: boolean;
  passwordStrength: boolean;
  email: boolean;
  phoneUsed: boolean;
  phone: boolean;
  ageInvalid: boolean;
  address: boolean;
};

const initialErrors: Errors = {
  general: false,
  passwordMatch: false,
  passwordStrength: false,
  email: false,
  phoneUsed: false,
  phone: false,
  ageInvalid: false,
  address: false,
};

const Signup: FC = () => {
  const [state, setState] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement
  > = (e) => {
    setErrors(initialErrors);
    if (e.target.name === 'gender') {
      return setState({
        ...state,
        gender:
          e.target.value === 'MALE_gender'
            ? 'MALE'
            : e.target.value === 'FEMALE_gender'
            ? 'FEMALE'
            : 'NOT_SPECIFIED',
      });
    }

    if (e.target.name === 'isHandicap') {
      return setState({
        ...state,
        isHandicap: e.target.checked,
      });
    }

    if (e.target.name === 'nationalityFrench') {
      return setState({
        ...state,
        nationality: e.target.checked ? 'Française' : '',
      });
    }
      
    if (e.target.name === 'phone') {
      let value = e.target.value.replace(/\D/g, '');
      value = value.substring(0, 10);
      const formattedValue = value.replace(/(\d{2})(?=\d)/g, '$1 ');
      return setState({
        ...state,
        phone: formattedValue,
      });
    }

    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const setCountry = useCallback(
    (value: string) => {
      setState({
        ...state,
        country: value,
      });
    },
    [state, setState],
  );

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    if (state.password !== state.passwordConfirm) {
      setLoading(false);
      setErrors({
        ...initialErrors,
        passwordMatch: true,
      });
      return;
    }

    if (!isPasswordValid(state.password)) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        passwordStrength: true,
      });
    }

    if (
      [state.address, state.region, state.postalCode, state.city, state.country].some(
        (value) => !value.trim(),
      )
    ) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        address: true,
      });
    }

    const newDateOfBirth = new Date(state.dateOfBirth);

    if (newDateOfBirth.toString() !== 'Invalid Date') {
      const today = new Date();
      let age = today.getFullYear() - newDateOfBirth.getFullYear();
      const monthDiff = today.getMonth() - newDateOfBirth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < newDateOfBirth.getDate())) {
        age--;
      }
      const maxAge = state.isHandicap ? 31 : 30;
      if (age < 16 || age > maxAge) {
        setLoading(false);
        return setErrors({
          ...initialErrors,
          ageInvalid: true,
        });
      }
    }

    publicAPI
      .post('/user/signup/mentee', {
        ...state,
        dateOfBirth:
          newDateOfBirth.toString() === 'Invalid Date' ? null : newDateOfBirth,
      })
      .then((res) => {
        const {
          data: { accessToken },
        } = res;
        setLoading(false);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('accessToken', accessToken);
          mutate('/auth/profile', undefined);
          navigate(
            '/authentication/verification-email-sent?next=/mentee/welcome',
          );
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err && err.response && err.response.status === 400) {
          if (err.response.data && err.response.data.fields) {
            if (err.response.data.fields.includes('email')) {
              return setErrors({
                ...initialErrors,
                email: true,
              });
            }
            if (
              ['address', 'region', 'postalCode', 'city', 'country'].some(
                (field) => err.response.data.fields.includes(field),
              )
            ) {
              return setErrors({
                ...initialErrors,
                address: true,
              });
            }
            if (err.response.data.fields.includes('phone')) {
              return setErrors({
                ...initialErrors,
                phoneUsed: true,
              });
            }
          }
          if (err.response.data?.message[0]?.includes('phone')) {
            return setErrors({
              ...initialErrors,
              phone: true,
            });
          }
        }
        return setErrors({
          ...initialErrors,
          general: true,
        });
      });
  };

  return (
    <AuthenticationLayout
      title="Bénéficier du Mentorat avec l'Institut de l'Engagement"
      backTo="/"
      src_image="/logo_institut_mentorat.png"
    >
      <Subtitle>
        <p>
          L'Institut de l'Engagement vous propose d'être mis en relation avec
          une personne bénévole qui, sans être un professionnel de
          l’accompagnement, va vous écouter, vous conseiller, faire écho à vos
          questions, vous donner un appui pour franchir une étape.{' '}
        </p>
        <p>
          Ce bénévole s'engage à vous consacrer du temps (au moins une
          rencontre, en présence ou à distance, toutes les 6 semaines, pendant 6
          mois).
        </p>
        <p>
          Il peut vous aider à remplir des dossiers de candidature (à l'Institut
          et ailleurs), prendre ou reprendre confiance, mieux définir votre
          projet de formation ou votre projet professionnel, réussir vos études,
          chercher un emploi, structurer un projet de création...
        </p>
        <p>
          Pour trouver le mentor qui sera votre interlocuteur de confiance, nous
          avons besoin d'informations. Merci de répondre aux questions qui
          suivent.
        </p>
      </Subtitle>

      <FormContainer>
        <Head
          title="Informations générales"
          subtitle="Liste toutes les informations sur l'utilisateur"
        />
        <Line />
        <Form onSubmit={handleSubmit}>
          {errors.general && (
            <GeneralErrorContainer>
              <Error>Une erreur s'est produite.</Error>
            </GeneralErrorContainer>
          )}
          <FormContent>
            <FormSection>
              <Input
                label="Nom *"
                placeholder="Nom"
                required
                value={state.lastName}
                onChange={handleChange}
                name="lastName"
              />
              <Input
                label="Prénom *"
                placeholder="Prénom"
                required
                value={state.firstName}
                onChange={handleChange}
                name="firstName"
              />
              {errors.email && (
                <Error>
                  Cette adresse email est invalide ou déjà utilisée.
                </Error>
              )}
              <Input
                label="Adresse email *"
                placeholder="exemple@mail.com"
                type="email"
                required
                value={state.email}
                onChange={handleChange}
                name="email"
              />
              {errors.phoneUsed && (
                <Error>Ce numéro de téléphone est déjà utilisé.</Error>
              )}
              {errors.phone && <Error>Ce numéro de téléphone invalide.</Error>}
              <Input
                label="Numéro de téléphone *"
                placeholder="01 23 45 67 89"
                required
                value={state.phone}
                onChange={handleChange}
                name="phone"
              />
              {errors.passwordMatch && (
                <Error>Les mots de passe ne correspondent pas.</Error>
              )}
              {errors.passwordStrength && (
                <Error>Le mot de passe n'est pas assez fort.</Error>
              )}
              <PasswordInput
                label="Mot de passe *"
                placeholder="Mot de passe"
                required
                value={state.password}
                onChange={handleChange}
                name="password"
              />
              <PasswordInput
                label="Confirmer votre mot de passe *"
                placeholder="Mot de passe"
                required
                value={state.passwordConfirm}
                onChange={handleChange}
                name="passwordConfirm"
              />
              <PasswordRequirements password={state.password} marginTop={1} />
            </FormSection>
            <FormSection>
              <Label>Vous êtes</Label>
              <RadiosContainer>
                <Radio
                  label="Un homme"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'MALE'}
                  id="MALE"
                />
                <Radio
                  label="Une femme"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'FEMALE'}
                  id="FEMALE"
                />
                <Radio
                  label="Autre"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'NOT_SPECIFIED'}
                  id="NOT_SPECIFIED"
                />
              </RadiosContainer>
<LabelledCheckboxContainer style={{ paddingBottom: '0.5rem' }}>
                  <LabelledCheckbox
                    label="Je suis en situation de handicap"
                    checked={state.isHandicap}
                    onChange={handleChange}
                    name="isHandicap"
                  />
                </LabelledCheckboxContainer>

                {state.isHandicap && (
                  <Input
                    label="Souhaitez-vous communiquer plus d’informations sur votre situation ?"
                    placeholder="Précisez ici si vous le souhaitez..."
                    value={state.handicapDetails}
                    onChange={handleChange}
                    name="handicapDetails"
                  />
                )}
              <Input
                label="Date de naissance *"
                type="date"
                placeholder="aaaa-mm-jj (ex: 2022-05-19)"
                value={state.dateOfBirth}
                onChange={handleChange}
                name="dateOfBirth"
                required
              />
              <AgeHint>
                Le programme est ouvert aux jeunes de 16 à 30 ans (31 ans en situation de handicap).
              </AgeHint>
              {errors.ageInvalid && (
                <Error>
                  Vous ne remplissez pas les conditions d'âge pour participer au programme (16-30 ans, ou jusqu'à 31 ans en situation de handicap).
                </Error>
              )}
              
              <Label style={{ marginTop: '1.5rem' }}>Nationalité *</Label>
              <LabelledCheckboxContainer style={{ paddingBottom: '0.25rem', paddingTop: '0.5rem' }}>
                <LabelledCheckbox
                  label="Française"
                  checked={state.nationality === 'Française'}
                  onChange={handleChange}
                  name="nationalityFrench"
                />
              </LabelledCheckboxContainer>
              {state.nationality !== 'Française' && (
                <Input
                  label="Autre nationalité *"
                  placeholder="Précisez votre nationalité..."
                  value={state.nationality}
                  onChange={handleChange}
                  name="nationality"
                  required
                />
              )}
              
              {errors.address && (
                <Error>
                  Veuillez renseigner votre adresse complète (adresse, région,
                  code postal, ville et pays).
                </Error>
              )}
              <Input
                label="Adresse *"
                placeholder="Numéro et rue"
                required
                value={state.address}
                onChange={handleChange}
                name="address"
              />
              <div style={{ marginBottom: '1.5rem' }}>
                <Label>Région *</Label>
                <select
                  name="region"
                  required
                  value={state.region}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    height: '42px',
                    padding: '0 0.8rem',
                    borderRadius: '5px',
                    border: '1px solid var(--gray-300)',
                    backgroundColor: 'white',
                    fontSize: '0.9rem',
                    color: state.region ? 'var(--gray-900)' : 'var(--gray-500)',
                    outline: 'none',
                    appearance: 'auto'
                  }}
                >
                  <option value="" disabled>Sélectionnez votre région</option>
                  {REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Code postal *"
                placeholder="Code postal"
                required
                value={state.postalCode}
                onChange={handleChange}
                name="postalCode"
              />
              <Input
                label="Ville *"
                placeholder="Ville"
                required
                value={state.city}
                onChange={handleChange}
                name="city"
              />
              <CountryInput
                value={state.country}
                setValue={setCountry}
                label="Pays *"
                required
              />
            </FormSection>
          </FormContent>
           
                <EmailNotice>
              ⚠️ En cliquant sur "S'inscrire", tu recevras un email pour valider ton adresse. Tu pourras ensuite remplir le questionnaire et signer la charte pour finaliser ton inscription au programme de mentorat.
              </EmailNotice>
              <Button color="primary" loading={loading}>
            S'inscrire
          </Button>
        </Form>
      </FormContainer>
    </AuthenticationLayout>
  );
};

export default Signup;
